import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, tap, startWith, Observable } from 'rxjs';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf, AsyncPipe } from '@angular/common';

import { LoadingSpinnerComponent } from '../../../../components/utils/loading-spinner.component';
import { DatacenterFilterApiService } from '../../../../services/datacenter-filter-api.service';
import { DocumentSuggestiveListFilterWithId } from '../../../../models/datacenter-filters';

interface ItemValue {
	id: string | undefined;
	code: string | undefined;
	entityName?: string | undefined;
}

@Component({
	selector: 'copo-doc-suggestive-item-code-filter',
	templateUrl: './doc-suggestive-item-code-filter.component.html',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		MatFormFieldModule,
		MatInputModule,
		MatAutocompleteModule,
		FormsModule,
		ReactiveFormsModule,
		MatIconModule,
		MatOptionModule,
		LoadingSpinnerComponent,
		AsyncPipe
	],
})
export class DocumentSuggestiveItemCodeFilterComponent implements OnInit {
	@Input() placeholder?: string;
	@Input() filterValue?: ItemValue;
	@Input() filter?: DocumentSuggestiveListFilterWithId;
	suggestiveCtrl = new FormControl('', { nonNullable: true });

	filteredItems?: Observable<ItemValue[]>;
	isLoading = false;

	constructor(
		private readonly datacenterFilterApiService: DatacenterFilterApiService
	) {}

	ngOnInit() {
		if (this.filterValue) {
			this.suggestiveCtrl.setValue(this.getValue(this.filterValue));
		}
		this.filteredItems = this.suggestiveCtrl.valueChanges
			.pipe(
				startWith(this.suggestiveCtrl.value),
				debounceTime(300),
				distinctUntilChanged(),
				tap(() => {
					this.isLoading = true;
				}),
				switchMap((query: string) =>
					this.datacenterFilterApiService.getDocumentBySearchTextOrId(
						this.filter,
						this.stripParenthesizedSuffix(query),
						this.getParenthesizedSuffix(query)
					)),
				tap(() => {
					this.isLoading = false;
				})
			);
	}

	truncateZeros(code: string) {
		return code.replace(/^0+/, '');
	}

	stripParenthesizedSuffix(value: string): string {
		const openParenIndex = value.lastIndexOf('(');
		if (openParenIndex === -1) {
			return value.trim();
		}
		return value.slice(0, openParenIndex).trim();
	}

	getParenthesizedSuffix(value: string): string | undefined {
		const openParenIndex = value.lastIndexOf('(');
		const closeParenIndex = value.lastIndexOf(')');
		if (openParenIndex === -1) {
			return undefined;
		}
		const endIndex = closeParenIndex === -1 || closeParenIndex < openParenIndex ? value.length : closeParenIndex;
		return value.slice(openParenIndex + 1, endIndex).trim();
	}

	getValue(proposition: ItemValue) {
		let value = '';
		if (proposition.id && proposition.code) {
			value = `${ proposition.code } (${ this.truncateZeros(proposition.id) })`;
		}
		return value;
	}

	propositionChanged(proposition: ItemValue) {
		if (!this.filterValue) {
			return;
		}
		Object.assign(this.filterValue, proposition);
	}
}
